import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { analyseTheme } from "../../analyse/analyseTheme";
import withContext from "../../../tools/withContext";
import {
  buildRecentProjectsPath,
  selectProjects,
} from "../hooks/batchQueries";
import { useWidgetQuery } from "../hooks/useWidgetQuery";
import { panelCardStyles } from "./sharedStyles";
import { HomepageProject } from "./types";
import { formatLabelingType } from "./utils";
import { RecentProjectsWidgetSkeleton } from "./WidgetSkeletons";

const EMPTY_PROJECTS: HomepageProject[] = [];

const styles = (theme: Theme) =>
  createStyles({
    ...panelCardStyles(theme),
    recentProjectsCard: {
      flex: 1,
      minWidth: 0,
    },
    projectClient: {
      width: 100,
      flexShrink: 0,
      fontSize: "0.8rem",
      color: analyseTheme.textSecondary,
      textAlign: "right",
    },
    projectType: {
      width: 110,
      flexShrink: 0,
      fontSize: "0.75rem",
      color: analyseTheme.textSecondary,
      textAlign: "right",
    },
    projectTotalBatches: {
      width: 56,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
      fontSize: "0.75rem",
      color: analyseTheme.textSecondary,
      fontWeight: 500,
    },
  });

interface Props {
  classes: Record<string, string>;
  isAdmin: boolean;
  ctx: { appFetch: (url: string) => Promise<any>; user: { id: number } };
}

const RecentProjectsWidget = ({
  classes,
  isAdmin,
  ctx: { appFetch, user },
}: Props) => {
  const path = useMemo(() => {
    if (!isAdmin || user?.id == null) {
      return null;
    }
    return buildRecentProjectsPath(user.id);
  }, [isAdmin, user?.id]);

  const { isLoading, data: recentProjects } = useWidgetQuery(
    path,
    appFetch,
    selectProjects,
    EMPTY_PROJECTS,
  );

  if (!isAdmin) {
    return null;
  }

  if (isLoading) {
    return <RecentProjectsWidgetSkeleton />;
  }

  return (
    <Paper
      elevation={0}
      className={`${classes.panelCard} ${classes.recentProjectsCard}`}
    >
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionHeaderTitle} variant="h6">
          Les derniers projets
        </Typography>
        <Link to="/projects/0?sort=createdDate,desc" className={classes.sectionLink}>
          Afficher tout
        </Link>
      </div>
      {recentProjects.length === 0 ? (
        <Typography className={classes.panelEmpty}>
          Aucun projet récent.
        </Typography>
      ) : (
        <div className={classes.activeBatchList}>
          {recentProjects.map((project) => {
            const hasBatches = project.totalBatches > 0;
            const content = (
              <>
                <Typography className={classes.activeBatchName} noWrap>
                  {project.name}
                </Typography>
                <Typography className={classes.projectClient} noWrap>
                  {project.clientName}
                </Typography>
                <Typography className={classes.projectType} noWrap>
                  {formatLabelingType(project.labelingType)}
                </Typography>
                <span className={classes.projectTotalBatches}>
                  <Typography component="span" noWrap>
                    {project.totalBatches}
                  </Typography>
                </span>
              </>
            );

            if (hasBatches) {
              return (
                <Link
                  key={project.id}
                  to={`/batches/0?projectId=${project.id}&sort=createdDate,desc`}
                  className={classes.activeBatchItem}
                >
                  {content}
                </Link>
              );
            }

            return (
              <div
                key={project.id}
                className={`${classes.activeBatchItem} ${classes.activeBatchItemDisabled}`}
              >
                {content}
              </div>
            );
          })}
        </div>
      )}
    </Paper>
  );
};

export default withContext(withStyles(styles)(RecentProjectsWidget));
